import type { Location, PracticeSettings } from "@allergy123/contracts/clinical";
import { Button, Card, Field, Select, Status } from "@allergy123/ui";
import { useState } from "react";
import { Link } from "react-router";
import { useBilling, useClinical, useLoad } from "../api";
import { ATTESTATIONS } from "../lib/attestations";
import { errorText, isForbidden, isUnavailable } from "../lib/errors";
import { planLabel, WEEKDAY_LABELS } from "../lib/format";
import { useSession } from "../session";

const SLOTS = [5, 10, 15, 20, 30, 60];

/** Practice settings: each location's hours, slot length, closed days and the panel in use (Q-18, C-48). Editing needs clinical:manage. */
export function SettingsPage() {
  const s = useSession();
  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-h1 font-semibold text-anchor">Practice settings</h1>
        <p className="max-w-[68ch] text-ink-secondary">
          {s.organizationName}. Hours and slots drive the calendar; the tray panel and allergen profile are set by Allergy 123 and shown here for reference.{!s.can("clinical:manage") && " Changing settings needs the manage permission."}
        </p>
      </div>
      <div className="flex flex-col gap-6">
        <BillingCard />
        {s.settings && <SignaturesCard key={`sig-${s.settings.version}`} settings={s.settings} editable={s.can("clinical:manage")} onSaved={s.reload} />}
        {s.locations.map((l) => (
          <LocationCard key={`${l.locationId}-${l.version}`} location={l} editable={s.can("clinical:manage")} onSaved={s.reload} />
        ))}
      </div>
    </>
  );
}

/**
 * The practice's billing account as the API lets it see it (ADR 0008): the plan in words, whether a payment method is
 * on file and how many orders are open. No card detail and no Stripe identifier ever reach this app; a payment method
 * is saved through a link Allergy 123 sends. The summary needs `clinical:manage`; `503 billing_unavailable` hides the card.
 */
function BillingCard() {
  const billing = useBilling();
  const s = useSession();
  const summary = useLoad(() => billing.summary(), [billing, s.organizationId]);
  if (summary.state === "error" && isUnavailable(summary.error)) {
    return <Status kind="info" label="Billing is not available in this environment, so the billing card is hidden." />;
  }
  return (
    <Card as="section" aria-labelledby="billing">
      <h2 id="billing" className="mb-2 text-h2 font-semibold text-anchor">
        Billing
      </h2>
      <p className="mb-4 max-w-[68ch] text-ink-secondary">
        Payment methods are added through a secure link Allergy 123 sends; nothing about a card is entered or stored here. Supplies are ordered on the <Link to="/supplies">Supplies</Link> page.
      </p>
      {summary.state === "error" ? (
        isForbidden(summary.error) ? (
          <Status kind="info" label="Billing details need the manage permission." />
        ) : (
          <Status kind="error" label={errorText(summary.error)} />
        )
      ) : !summary.data ? (
        <p className="text-ink-secondary">Loading…</p>
      ) : !summary.data.account ? (
        <p className="text-ink-secondary">No billing account has been set up for this practice yet; Allergy 123 creates it. Ordering supplies online needs one.</p>
      ) : (
        <dl className="grid max-w-[48ch] grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-body">
          <dt className="text-ink-secondary">Plan</dt>
          <dd>{planLabel(summary.data.account.plan)}</dd>
          <dt className="text-ink-secondary">Payment method on file</dt>
          <dd>{summary.data.account.paymentMethodStatus === "saved" ? "Yes" : "No"}</dd>
          <dt className="text-ink-secondary">Open orders</dt>
          <dd>{summary.data.openOrders}</dd>
          {summary.data.account.status !== "active" && (
            <>
              <dt className="text-ink-secondary">Account</dt>
              <dd>
                <Status kind="warning" label={summary.data.account.status === "suspended" ? "Suspended: contact Allergy 123." : "Closed: contact Allergy 123."} />
              </dd>
            </>
          )}
        </dl>
      )}
    </Card>
  );
}

function LocationCard({ location, editable, onSaved }: { location: Location; editable: boolean; onSaved: () => void }) {
  const api = useClinical();
  const panel = useLoad((a) => (location.trayPanelId ? a.reference.locationPanel(location.locationId) : Promise.resolve(null)), [location.locationId, location.trayPanelId]);
  const [form, setForm] = useState({ timezone: location.timezone, ...location.schedule, closedWeekdays: [...location.schedule.closedWeekdays] });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const dirty = JSON.stringify(form) !== JSON.stringify({ timezone: location.timezone, ...location.schedule });

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      const { timezone, ...schedule } = form;
      await api.locations.updateSettings(location.locationId, { version: location.version, timezone, schedule: { ...schedule, slotMinutes: schedule.slotMinutes as 5 | 10 | 15 | 20 | 30 | 60, closedWeekdays: [...schedule.closedWeekdays].sort() } });
      setNote({ kind: "success", text: "Saved." });
      onSaved();
    } catch (err) {
      setNote({ kind: "error", text: errorText(err) });
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" aria-labelledby={`loc-${location.locationId}`}>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={`loc-${location.locationId}`} className="text-h2 font-semibold text-anchor">
          {location.name}
        </h2>
        <span className="text-small text-ink-secondary">settings version {location.version}</span>
      </div>
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => void save(e)}>
          <Field label="Timezone (IANA)" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} disabled={!editable} hint="Times on the wire are UTC; this is how they are shown." />
          <div />
          <Field label="Opens" type="time" step={300} value={form.opensAt} onChange={(e) => setForm({ ...form, opensAt: e.target.value })} disabled={!editable} />
          <Field label="Closes (last slot)" type="time" step={300} value={form.closesAt} onChange={(e) => setForm({ ...form, closesAt: e.target.value })} disabled={!editable} />
          <Select label="Slot length (minutes)" options={SLOTS.map((m) => ({ value: String(m), label: String(m) }))} value={String(form.slotMinutes)} onChange={(e) => setForm({ ...form, slotMinutes: Number(e.target.value) as typeof form.slotMinutes })} disabled={!editable} />
          <Field label="Default duration (minutes)" type="number" min={5} max={480} step={5} value={form.defaultDurationMinutes} onChange={(e) => setForm({ ...form, defaultDurationMinutes: Number(e.target.value) })} disabled={!editable} />
          <fieldset className="sm:col-span-2" disabled={!editable}>
            <legend className="mb-2 text-small font-medium text-ink">Closed on</legend>
            <div className="flex flex-wrap gap-4">
              {WEEKDAY_LABELS.map((label, i) => (
                <label key={label} className="flex items-center gap-2 text-body">
                  <input type="checkbox" className="h-4 w-4 accent-[var(--cta-bg)]" checked={form.closedWeekdays.includes(i + 1)} onChange={(e) => setForm({ ...form, closedWeekdays: e.target.checked ? [...form.closedWeekdays, i + 1] : form.closedWeekdays.filter((d) => d !== i + 1) })} />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          {note && <Status kind={note.kind} label={note.text} className="sm:col-span-2" />}
          {editable && (
            <div className="sm:col-span-2">
              <Button type="submit" disabled={busy || !dirty}>
                Save settings
              </Button>
            </div>
          )}
        </form>
        <Card tone="tinted" dense>
          <h3 className="mb-2 text-h3 font-semibold text-anchor">Testing panel</h3>
          {!location.trayPanelId ? (
            <p className="text-ink-secondary">No tray panel is set for this location; tests cannot start until Allergy 123 assigns one.</p>
          ) : panel.state === "error" ? (
            <Status kind="warning" label={`Panel unavailable: ${errorText(panel.error)}`} />
          ) : panel.data ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-body">
              <dt className="text-ink-secondary">Tray panel</dt>
              <dd>
                {panel.data.trayPanel.name} v{panel.data.trayPanel.version}
              </dd>
              <dt className="text-ink-secondary">Allergen profile</dt>
              <dd>
                {panel.data.allergenProfile.name} v{panel.data.allergenProfile.version}
              </dd>
              <dt className="text-ink-secondary">Timers</dt>
              <dd>
                {panel.data.timers.histamineReadMinutes} and {panel.data.timers.fullReadMinutes} minutes
              </dd>
            </dl>
          ) : (
            <p className="text-ink-secondary">Loading…</p>
          )}
        </Card>
      </div>
    </Card>
  );
}

const SIGNATURE_KINDS = Object.keys(ATTESTATIONS).filter((k) => !k.startsWith("mixing_")) as PracticeSettings["signatureKinds"];

/** Which signatures the practice collects and the statement each signer sees (D-014, Q-89). */
function SignaturesCard({ settings, editable, onSaved }: { settings: PracticeSettings; editable: boolean; onSaved: () => void }) {
  const api = useClinical();
  const [collect, setCollect] = useState(settings.collectSignatures);
  const [kinds, setKinds] = useState<string[]>(settings.signatureKinds);
  const [statements, setStatements] = useState<PracticeSettings["statements"]>(settings.statements);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const dirty = collect !== settings.collectSignatures || JSON.stringify([...kinds].sort()) !== JSON.stringify([...settings.signatureKinds].sort()) || JSON.stringify(statements) !== JSON.stringify(settings.statements);
  const bump = (kind: string, text: string) => {
    const prev = statements[kind];
    const n = prev ? Number(prev.version.split("-v")[1] ?? 0) + 1 : 1;
    setStatements({ ...statements, [kind]: { text, version: `${kind}-v${n}` } });
  };

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      await api.settings.update({ version: settings.version, collectSignatures: collect, signatureKinds: kinds as PracticeSettings["signatureKinds"], statements });
      setNote({ kind: "success", text: "Saved." });
      onSaved();
    } catch (err) {
      setNote({ kind: "error", text: errorText(err) });
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" aria-labelledby="signatures">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="signatures" className="text-h2 font-semibold text-anchor">
          Signatures
        </h2>
        <span className="text-small text-ink-secondary">settings version {settings.version}</span>
      </div>
      <p className="mb-4 max-w-[68ch] text-ink-secondary">Allergy 123 ships supplies; signatures are the practice's choice. Only the kinds enabled here are required before an encounter is signed, and the statement text below is what the signer sees. Each change to a text gets a new version, recorded on every signature taken under it.</p>
      <form className="flex flex-col gap-4" onSubmit={(e) => void save(e)}>
        <label className="flex items-center gap-2 text-body">
          <input type="checkbox" className="h-4 w-4 accent-[var(--cta-bg)]" checked={collect} onChange={(e) => setCollect(e.target.checked)} disabled={!editable} />
          Collect signatures at this practice
        </label>
        {collect && (
          <div className="grid gap-3 lg:grid-cols-2">
            {SIGNATURE_KINDS.map((kind) => {
              const copy = ATTESTATIONS[kind]!;
              const on = kinds.includes(kind);
              return (
                <fieldset key={kind} className="rounded-md border border-rule-subtle p-3" disabled={!editable}>
                  <legend className="px-1 text-small font-medium text-ink">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" className="h-4 w-4 accent-[var(--cta-bg)]" checked={on} onChange={(e) => setKinds(e.target.checked ? [...kinds, kind] : kinds.filter((k) => k !== kind))} />
                      {copy.label} <span className="font-normal text-ink-secondary">· {copy.signer}</span>
                    </label>
                  </legend>
                  {on && (
                    <label className="flex flex-col gap-1 text-small text-ink-secondary">
                      Statement{statements[kind] ? ` · ${statements[kind]!.version}` : " · default placeholder"}
                      <textarea className="min-h-16 rounded-sm border border-rule-strong bg-card px-3 py-2 text-body text-ink" value={statements[kind]?.text ?? copy.statement} onChange={(e) => bump(kind, e.target.value)} maxLength={1000} />
                    </label>
                  )}
                </fieldset>
              );
            })}
          </div>
        )}
        {note && <Status kind={note.kind} label={note.text} />}
        {editable && (
          <div>
            <Button type="submit" disabled={busy || !dirty}>
              Save signature settings
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}
